import type { DogBreederWhereInput } from '@gql/graphql';
import type { BreederListPageFormValues } from '@presentation/pages';
import { intersection } from 'lodash/fp';
import { useCallback } from 'react';
import { useLitterWhereBuilder } from '../../LitterListPageController/api/useLitterWhereBuilder';

export const useBreedersWhereBuilder = () => {
  const { litterWhereBuilder } = useLitterWhereBuilder();

  const breedersWhereBuilder = useCallback(
    (form?: BreederListPageFormValues): DogBreederWhereInput => {
      const whereInputs: DogBreederWhereInput[] = [];

      whereInputs.push({
        certified: { equals: true },
      });

      whereInputs.push({
        public: { equals: true },
      });

      if (form) {
        if (form.breedNames?.length) {
          whereInputs.push({
            breederBreeds: {
              some: {
                dogBreed: {
                  is: {
                    OR: [
                      { name: { in: form.breedNames } },
                      { baseBreedName: { in: form.breedNames } },
                    ],
                  },
                },
              },
            },
          });
        }

        if (form.levels?.length) {
          whereInputs.push({
            level: { in: form.levels },
          });
        }

        if (form.hasLitters != null) {
          if (form.hasLitters) {
            whereInputs.push({ litters: { some: litterWhereBuilder() } });
          } else {
            whereInputs.push({ litters: { none: litterWhereBuilder() } });
          }
        }

        if (form.spokenLanguages?.length) {
          whereInputs.push({ spokenLanguages: { hasSome: form.spokenLanguages } });
        }

        if (form.pedigree?.length) {
          const hasFci = !!intersection(form.pedigree, ['id1', 'FCI', 'fci']).length;
          const hasOther = form.pedigree.includes('OTHER');
          if (hasFci && hasOther) {
            whereInputs.push({ dogsPedigree: { not: { equals: 'no_pedigree' } } });
            whereInputs.push({ dogsPedigree: { not: { equals: null } } });
          } else if (hasFci) {
            whereInputs.push({ dogsPedigree: { in: ['FCI', 'fci', 'id1'] } });
          } else if (hasOther) {
            whereInputs.push({
              dogsPedigree: { not: { in: ['', 'FCI', 'fci', 'id1', 'no_pedigree'] } },
            });
            whereInputs.push({ dogsPedigree: { not: { equals: null } } });
          }
        }

        if (form.countries?.length) {
          whereInputs.push({
            account: { is: { address: { is: { country: { in: form.countries } } } } },
          });
        }
        if (form.regions?.length) {
          whereInputs.push({
            account: { is: { address: { is: { region: { in: form.regions } } } } },
          });
        }
      }

      return { ...(whereInputs.length ? { AND: whereInputs } : {}) } as DogBreederWhereInput;
    },
    [litterWhereBuilder],
  );

  return { breedersWhereBuilder };
};
